import { useCreateSetupIntent, useUpdateSubscription } from "@/api/stripe";
import { useUserInfo } from "@/api/user";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useExistingSubscription } from "@/hooks/useExistingSubscription";
import { useStripeAppearance } from "@/hooks/useStripeAppearance";
import { cn } from "@/utils";
import {
  trackBillingCheckoutCompleted,
  trackBillingCheckoutFailed,
  trackBillingCheckoutSubmitted,
} from "@/utils/analytics";
import {
  buildPaymentMethodUpdate,
  confirmSetupReturnUrl,
  paymentMethodIdFrom,
  type PaymentMethodIntent,
} from "@/utils/confirmSetup";
import { stripePromise } from "@/utils/stripe";
import type { BillingInterval } from "@cohesive/api-client";
import {
  AddressElement,
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type { StripeElementsOptions } from "@stripe/stripe-js";
import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

/**
 * Everything the dialog needs, captured at open time. Non-nullable on purpose:
 * it makes `buildPaymentMethodUpdate`'s null branch — a card that attaches
 * without becoming the default — unreachable from this surface.
 */
export interface PaymentMethodDialogSession extends PaymentMethodIntent {
  planName: string;
  billingCycle: BillingInterval;
  userCount: number;
  /** Where a *redirect* round-trip lands after /billing/confirm-setup. */
  redirectTo: string;
}

export interface PaymentMethodDialogProps {
  open: boolean;
  session: PaymentMethodDialogSession;
  /** `NESTED_DIALOG_Z` from a pane or banner, `STACKED_DIALOG_Z` over the manage-plan modal. */
  zIndexClassName?: string;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}

/**
 * Stripe's 3DS challenge is a body child, outside this layer: the iframe
 * itself, and the wrapper `div` that positions it — whose own backdrop
 * surrounds the frame and is just as much part of the challenge.
 */
const STRIPE_CHALLENGE = [
  'iframe[name^="__privateStripeFrame"]',
  'div:has(> iframe[name^="__privateStripeFrame"])',
].join(", ");

/**
 * Collects a card without leaving Settings: its own Stripe Elements over a
 * SetupIntent minted per open, then the same subscription write
 * `ConfirmSetupPage` performs when Stripe takes the tab.
 */
export function PaymentMethodDialog({
  open,
  session,
  zIndexClassName,
  onOpenChange,
  onSaved,
}: PaymentMethodDialogProps) {
  // Held above the content so the dismissal guards can read it; the form
  // unmounts with the content it would otherwise live in.
  const [isSubmitting, setIsSubmitting] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="payment-method-dialog"
        className={cn("!max-w-lg", zIndexClassName)}
        overlayClassName={zIndexClassName}
        closeButtonDisabled={isSubmitting}
        // A payment mid-flight must not be dismissed from outside: the card is
        // attached at Stripe before the subscription write lands, and closing
        // takes the retry with it.
        onEscapeKeyDown={(event) => {
          if (isSubmitting) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (isSubmitting) {
            event.preventDefault();
            return;
          }
          const target = event.target as HTMLElement | null;
          if (target?.closest(STRIPE_CHALLENGE)) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {session.isPaymentMethodUpdate
              ? "Update payment method"
              : "Add a payment method"}
          </DialogTitle>
          <DialogDescription>
            Card details go straight to Stripe.
          </DialogDescription>
        </DialogHeader>
        {/* Mounted inside the content, so opening and closing the dialog is
            what mints and tears down the intent, the Stripe iframes and the
            error state. */}
        <PaymentMethodDialogBody
          session={session}
          isSubmitting={isSubmitting}
          onSubmittingChange={setIsSubmitting}
          onOpenChange={onOpenChange}
          onSaved={onSaved}
        />
      </DialogContent>
    </Dialog>
  );
}

function PaymentMethodFormSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-[190px] w-full" />
      <Skeleton className="h-[190px] w-full" />
      <div className="flex justify-end gap-2">
        <Skeleton className="h-9 w-20" />
        <Skeleton className="h-9 w-44" />
      </div>
    </div>
  );
}

function PaymentMethodDialogBody({
  session,
  isSubmitting,
  onSubmittingChange,
  onOpenChange,
  onSaved,
}: {
  session: PaymentMethodDialogSession;
  isSubmitting: boolean;
  onSubmittingChange: (submitting: boolean) => void;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}) {
  const createSetupIntent = useCreateSetupIntent();
  const appearance = useStripeAppearance();

  // The secret is held here, not read back off the mutation: React's
  // double-invoked mount detaches the observer that fired, so its result lands
  // nowhere and the form never appears.
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isMinting, setIsMinting] = useState(true);
  const [mintFailed, setMintFailed] = useState(false);

  const mintSetupIntent = async () => {
    setMintFailed(false);
    setIsMinting(true);
    try {
      const response = await createSetupIntent.mutateAsync();
      setClientSecret(response.data.client_secret);
    } catch {
      setMintFailed(true);
    } finally {
      setIsMinting(false);
    }
  };

  // A mutation, not the `setupIntentClientSecret` query: a query hands every
  // refetch trigger — focus, reconnect, remount, a prefix invalidation — the
  // power to mint a new intent behind already-mounted Elements, whose
  // clientSecret react-stripe-js treats as immutable. Confirming Elements for
  // one intent against another fails at Stripe.
  const mintedRef = useRef(false);
  useEffect(() => {
    if (mintedRef.current) return;
    mintedRef.current = true;
    void mintSetupIntent();
  }, []);

  const options = useMemo<StripeElementsOptions>(
    () => ({ clientSecret: clientSecret!, currency: "usd", appearance }),
    [clientSecret, appearance],
  );

  if (mintFailed) {
    return (
      <div className="flex flex-col gap-4">
        <Alert variant="destructive">
          <AlertDescription>
            Could not reach Stripe to start the card form.
          </AlertDescription>
        </Alert>
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void mintSetupIntent()}
            isLoading={isMinting}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return <PaymentMethodFormSkeleton />;
  }

  return (
    // Keyed as insurance: the mutation cannot change the secret under a mounted
    // provider anyway. Not keyed on `appearance` — that one is live-updatable.
    <Elements key={clientSecret} stripe={stripePromise} options={options}>
      <PaymentMethodForm
        session={session}
        clientSecret={clientSecret}
        isSubmitting={isSubmitting}
        onSubmittingChange={onSubmittingChange}
        onOpenChange={onOpenChange}
        onSaved={onSaved}
      />
    </Elements>
  );
}

function PaymentMethodForm({
  session,
  clientSecret,
  isSubmitting,
  onSubmittingChange,
  onOpenChange,
  onSaved,
}: {
  session: PaymentMethodDialogSession;
  clientSecret: string;
  isSubmitting: boolean;
  onSubmittingChange: (submitting: boolean) => void;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { data: userInfo } = useUserInfo();
  const { hasExistingSubscription, existingSubscriptionStatus } =
    useExistingSubscription();
  const updateSubscription = useUpdateSubscription();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Once confirmSetup resolves the card is attached at Stripe. Re-confirming a
  // succeeded intent is rejected outright, so a retry after a failed
  // subscription write must resume from here instead.
  const paymentMethodIdRef = useRef<string | null>(null);

  const analyticsFields = {
    planName: session.planName,
    billingInterval: session.billingCycle,
    seatCount: session.userCount,
    isPaymentMethodUpdate: session.isPaymentMethodUpdate,
    organization_id: userInfo?.organization?.id,
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setErrorMessage(null);
    onSubmittingChange(true);
    trackBillingCheckoutSubmitted(analyticsFields);

    try {
      if (!paymentMethodIdRef.current) {
        const { error: submitError } = await elements.submit();
        if (submitError) throw new Error(submitError.message);

        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          clientSecret,
          confirmParams: {
            return_url: confirmSetupReturnUrl({
              intent: session,
              redirectTo: session.redirectTo,
            }),
          },
          redirect: "if_required",
        });
        if (error) throw new Error(error.message);
        // Stripe took over the tab: /billing/confirm-setup finishes the job.
        if (!setupIntent) return;

        paymentMethodIdRef.current = paymentMethodIdFrom(setupIntent);
      }

      const paymentMethodId = paymentMethodIdRef.current;
      if (!paymentMethodId) throw new Error("Invalid payment method");

      const payload = buildPaymentMethodUpdate({
        intent: session,
        paymentMethodId,
        hasExistingSubscription,
        existingSubscriptionStatus,
      });
      // Non-null by the session's own types; the guard is for the compiler.
      if (payload) await updateSubscription.mutateAsync(payload);

      trackBillingCheckoutCompleted({
        ...analyticsFields,
        wasTrialing: existingSubscriptionStatus === "trialing",
      });
      toast.success("Payment method saved.");
      onSaved?.();
      onOpenChange(false);
    } catch (caught) {
      const message =
        caught instanceof Error
          ? caught.message
          : "Could not save the payment method.";
      setErrorMessage(message);
      trackBillingCheckoutFailed({ ...analyticsFields, error: message });
    } finally {
      onSubmittingChange(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* A pair: `billingDetails.name: "never"` means the name comes from the
          address element, and confirmSetup fails without it. */}
      <PaymentElement
        options={{
          layout: "tabs",
          fields: { billingDetails: { name: "never" } },
        }}
      />
      <AddressElement options={{ mode: "billing" }} />
      {errorMessage && (
        <Alert variant="destructive">
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => onOpenChange(false)}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          data-testid="submit-button"
          disabled={!stripe}
          isLoading={isSubmitting}
        >
          Save payment method
        </Button>
      </div>
    </form>
  );
}
